import type { LivesAt } from './api/navigation.ts';

/**
 * The town line in full (Arthur, A-#117): the neighbourhood if there is one, the city or borough, then the state and
 * ZIP, as "Canarsie, Brooklyn, NY 11236". The neighbourhood is left out where it is the city again, so no word is
 * doubled, and a missing part leaves no stray comma.
 */
export function townLine(at: Pick<LivesAt, 'neighbourhood' | 'city' | 'state' | 'zip'>): string {
  const city = at.city?.trim() || null;
  const hood = at.neighbourhood?.trim() || null;
  const sameAsCity = hood && city && hood.toLowerCase() === city.toLowerCase();
  const stateZip = [at.state?.trim(), at.zip?.trim()].filter(Boolean).join(' ');
  return [sameAsCity ? null : hood, city, stateZip].filter(Boolean).join(', ');
}

/**
 * Lives at (Arthur, 23 October, A-#104), as an address, wrapping and never cut short:
 * - a facility: its name, then its address on the next line (street, then the town line), as the Contacts panel's
 *   Facility line has it;
 * - a home or a family member's home: the address alone (street and unit, then the town line). Never a name made up
 *   for a home ("<name>'s apartment"): that reads as a person, not as where to find them.
 * The card adds where a temporary stay has them now (away_at), as the banner said it before.
 */
export function livesAtLines(at: LivesAt): string[] {
  const street = [at.street, at.unit].filter(Boolean).join(', ');
  const town = townLine(at);
  const lines = at.kind === 'facility'
    ? [at.name ?? 'Facility not named', [at.street, town].filter(Boolean).join(', ')]
    : [street, town];
  return lines.filter((l) => l !== '');
}
